<template>
  <div>
    <SettingsPageTemplate
      title="開発者向け設定"
      :tabs="tabs"
      :active-tab="activeTab"
      max-width="full"
      padding="sm"
      @tab-change="$emit('change-tab', $event)"
    >
      <template #info_settings>
        <div class="tab-content">
          <!-- 通常表示モード -->
          <div v-if="!isEditMode">
            <div class="section-title">
              <ContentText size="lg" color="default">各種情報設定</ContentText>
            </div>

            <InfoTableSection
              title="○位置情報"
              :headers="['施設名', '位置情報', '操作']"
              :action-buttons="[
                { label: '編集', variant: 'success', action: 'edit' },
                { label: 'クリア', variant: 'danger', action: 'clear' }
              ]"
              :locations="locationData"
              @edit="$emit('edit-location', $event)"
              @clear="$emit('clear-location', $event)"
            />

            <div class="coordinate-settings">
              <BaseButton
                @click="$emit('open-coordinate-settings')"
                variant="secondary"
                class="coordinate-btn"
              >
                座標変換補正値設定
              </BaseButton>
            </div>
          </div>

          <!-- 位置編集モード -->
          <div v-else class="edit-mode">
            <div class="edit-header">
              <ContentText size="lg" color="default">
                {{ currentEditingLocation ? currentEditingLocation.name : '位置情報' }}の位置設定
              </ContentText>
            </div>

            <div class="map-edit-container">
              <MapDisplay
                :markers="editMapMarkers"
                :center="editMapCenter"
                :zoom="editMapZoom"
                :enable-marker-drag="true"
                @map-click="$emit('map-click', $event)"
                @marker-drag-end="$emit('marker-drag-end', $event)"
              >
                <template #overlay>
                  <BaseButton
                    @click="$emit('exit-edit-mode')"
                    variant="primary"
                    class="exit-edit-btn"
                  >
                    位置設定を終了する
                  </BaseButton>
                </template>
              </MapDisplay>
            </div>
          </div>
        </div>
      </template>

      <template #account_settings>
        <div class="tab-content">
          <div class="section-title">
            <ContentText size="lg" color="default">アカウント設定</ContentText>
          </div>

          <!-- アカウント一覧セクション -->
          <div class="account-section">
            <!-- 既存のInfoTableSectionコンポーネントを再利用 -->
            <InfoTableSection
              title="アカウント一覧"
              :headers="accountHeaders"
              :locations="formattedAccounts"
              :action-buttons="accountActionButtons"
              @action="$emit('account-action', $event)"
            >
              <!-- アカウント専用のテーブル行スロット -->
              <template #tableRow="{ item, handleAction }">
                <BTd>
                  <ContentText color="default">
                    {{ item.name }}
                  </ContentText>
                </BTd>
                <BTd>
                  <ContentText color="default">
                    {{ item.attribute }}
                  </ContentText>
                </BTd>
                <BTd>
                  <ContentText color="default">
                    {{ item.company }}
                  </ContentText>
                </BTd>
                <BTd>
                  <ContentText color="muted">
                    {{ maskPassword(item.password) }}
                  </ContentText>
                </BTd>
                <BTd class="actions-cell">
                  <div class="action-buttons">
                    <BButton
                      v-for="action in accountActionButtons"
                      :key="action.action"
                      :variant="action.variant"
                      size="sm"
                      @click="handleAction(action.action, item)"
                      :class="`${action.action}-btn`"
                    >
                      {{ action.label }}
                    </BButton>
                  </div>
                </BTd>
              </template>
            </InfoTableSection>

            <!-- 新規追加ボタン -->
            <div class="add-button-container">
              <BaseButton
                @click="$emit('show-create-modal')"
                variant="primary"
              >
                新規追加
              </BaseButton>
            </div>
          </div>
        </div>
      </template>
    </SettingsPageTemplate>
  </div>
</template>

<script setup>
import BaseButton from '@/components/atoms/BaseButton.vue'
import BaseInput from '@/components/atoms/BaseInput.vue'
import ContentText from '@/components/atoms/ContentText.vue'
import ContentCard from '@/components/molecules/ContentCard.vue'
import InfoTableSection from '@/components/organisms/InfoTableSection.vue'
import MapDisplay from '@/components/organisms/MapDisplay.vue'
import SettingsPageTemplate from '@/components/templates/SettingsPageTemplate.vue'
import { maskPassword } from '@/utils/stringUtils.js'


// コンテナから渡されるデータをpropsとして定義します
defineProps({
  tabs: { type: Array, required: true },
  activeTab: { type: String, required: true },
  settings: { type: Object, required: true },
  isEditMode: { type: Boolean, default: false },
  currentEditingLocation: { type: Object, default: null },
  locationData: { type: Array, required: true },
  editMapMarkers: { type: Array, default: () => [] },
  editMapCenter: { type: Array, required: true },
  editMapZoom: { type: Number, required: true },
  accounts: { type: Array, default: () => [] }, // アカウント一覧データ
  // アカウント表示用の変換済みデータ（コンテナから受け取る）
  accountHeaders: { type: Array, default: () => [] },
  accountActionButtons: { type: Array, default: () => [] },
  formattedAccounts: { type: Array, default: () => [] },
})

// emitsは定義していませんが、テンプレートで$emitを使用している場合は
// ここで宣言する必要があります
defineEmits([
  'change-tab',
  'edit-location',
  'clear-location',
  'open-coordinate-settings',
  'map-click',
  'marker-drag-end',
  'exit-edit-mode',
  'account-action',
  'show-create-modal'
])
</script><style scoped>
/* (スタイルは変更なし) */
.tab-content {
  padding: 0.5rem 0;
}
.edit-mode .tab-content {
  padding: 0.25rem 0;
}
.section-title {
  margin-left: 2rem;
  margin-bottom: 1rem;
}
.form-group {
  margin-bottom: 1rem;
}
.form-group:last-child {
  margin-bottom: 0;
}
.coordinate-settings {
  margin-top: 1.5rem;
  margin-left: 2rem;
}
.coordinate-btn {
  min-width: 200px;
}
.edit-mode {
  padding: 0;
}
.edit-header {
  margin-left: 2rem;
  margin-bottom: 0.5rem;
}
.map-edit-container {
  margin: 0;
  height: 75vh;
  min-height: 600px;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}
.exit-edit-btn {
  min-width: 160px;
  font-weight: 600;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}
.exit-edit-btn:hover {
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
  transform: translateY(-1px);
}
/* (以下、メディアクエリも変更なし) */
/* アカウント設定セクションのスタイル */
.account-section {
  margin: 1.5rem 0; /* 左右のマージンを削除してInfoTableSectionのマージンを使用 */

  /* アカウント一覧の操作セルスタイル */
  .actions-cell {
    text-align: center;
    min-width: 160px;
  }

  .action-buttons {
    display: flex;
    gap: 0.75rem;
    justify-content: center;

    .edit-btn,
    .delete-btn {
      min-width: 70px;
      font-weight: 500;
      border-radius: 0.5rem;
      padding: 0.5rem 1rem;
      transition: all 0.2s ease;
      border: 1px solid transparent;
      font-size: 0.875rem;

      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      &:active {
        transform: translateY(0);
      }
    }
  }
}
.add-button-container {
  text-align: left;
  margin-top: 1.5rem;
  margin-left: 2rem; /* ボタンのみ左マージンを追加してInfoTableSectionと揃える */
}
@media (max-width: 768px) {
    .section-title, .coordinate-settings, .edit-header {
        margin-left: 1rem;
    }
    .account-section {
        margin: 1rem 0; /* モバイルでも左右マージンを削除 */

        .actions-cell {
            min-width: 140px;
        }

        .action-buttons {
            flex-direction: column;
            gap: 0.5rem;

            .edit-btn,
            .delete-btn {
                min-width: auto;
                width: 100%;
                padding: 0.625rem 1rem;
            }
        }
    }
    .add-button-container {
        margin-left: 1rem; /* モバイルではボタンのマージンも調整 */
    }
}
</style>
